import React from 'react';
import { Link } from 'react-router-dom';

const PILLARS = [
  {
    icon: '🏠', title: 'No data leaves your network', color: '#6366f1',
    body: 'DepGuard runs as a single Go binary with a local SQLite database. Your SBOMs, vulnerability data, and reports never leave your infrastructure. There is no SaaS backend to breach.',
    points: ['Local SQLite database, no external DB connections', 'Binary runs on your CI runner or on-premises server', 'No telemetry collected by default', 'Enterprise: fully air-gapped deployment support'],
  },
  {
    icon: '🔐', title: 'JWT authentication + RBAC', color: '#8b5cf6',
    body: 'Every API endpoint is protected by JWT tokens. Three roles (Admin, Analyst, Viewer) give you fine-grained access control across your team without managing separate credentials.',
    points: ['JWT HS256 tokens via golang-jwt/v5', 'Admin / Analyst / Viewer roles', 'Token expiry and refresh enforced', 'All auth events logged in audit trail'],
  },
  {
    icon: '📋', title: 'Complete audit trail', color: '#06b6d4',
    body: 'Every action (scan, policy change, VEX statement, remediation update) is logged with timestamp, user, and full payload. The immutable audit log satisfies EU CRA Article 20 retention requirements.',
    points: ['Timestamped action log', 'User attribution on every change', 'VEX statement audit trail', '10-year retention support on Enterprise'],
  },
  {
    icon: '🛡️', title: 'Secure supply chain practices', color: '#10b981',
    body: 'We eat our own dog food. Every DepGuard release is scanned by DepGuard itself. Our own SBOM is published with each release, verifiable via SLSA provenance attestation.',
    points: ['SBOM published per release', 'Self-scanned before shipping', 'SLSA Level 2 provenance (roadmap)', 'Signed binaries (cosign, roadmap)'],
  },
];

const COMPLIANCE_MAP = [
  { framework: 'EU CRA Article 20', requirement: 'Technical documentation including SBOM', coverage: 'Full' },
  { framework: 'EU CRA Article 20', requirement: 'Vulnerability handling & disclosure process', coverage: 'Full' },
  { framework: 'NIST SP 800-53 SA-10', requirement: 'Developer Configuration Management', coverage: 'Full' },
  { framework: 'NIST SP 800-53 SI-2', requirement: 'Flaw Remediation', coverage: 'Full' },
  { framework: 'NIST SP 800-53 SR-3', requirement: 'Supply Chain Controls and Processes', coverage: 'Full' },
  { framework: 'ISO 27001 A.12.6', requirement: 'Management of technical vulnerabilities', coverage: 'Full' },
  { framework: 'ISO 27001 A.15.1', requirement: 'Information security in supplier relationships', coverage: 'Partial' },
  { framework: 'SLSA Level 1', requirement: 'Provenance exists', coverage: 'Roadmap' },
];

function coverageChip(c) {
  const styles = {
    Full:    { bg: 'rgba(16,185,129,.1)',  color: '#065f46', border: 'rgba(16,185,129,.25)' },
    Partial: { bg: 'rgba(245,158,11,.1)', color: '#78350f', border: 'rgba(245,158,11,.25)' },
    Roadmap: { bg: 'rgba(99,102,241,.1)', color: '#3730a3', border: 'rgba(99,102,241,.25)' },
  }[c] || {};
  return (
    <span style={{
      fontSize:11, fontWeight:700, padding:'3px 10px', borderRadius:100,
      background: styles.bg, color: styles.color, border:`1px solid ${styles.border}`,
    }}>{c}</span>
  );
}

export default function WebsiteSecurityPage() {
  return (
    <div className="dgw-page-wrap">

      {/* Hero */}
      <section className="dgw-hero" style={{textAlign:'center'}}>
        <div className="dgw-hero-inner" style={{maxWidth:820}}>
          <div className="dgw-badge">
            <span className="dgw-badge-dot" />
            Security & Trust
          </div>
          <h1>Security isn't just what<br/>we scan. It's how we operate.</h1>
          <p className="dgw-hero-lead" style={{maxWidth:620, margin:'0 auto 40px'}}>
            A security product that you cannot trust is worse than no product. Here's exactly how 
            DepGuard handles your data, authenticates your team, and meets compliance requirements.
          </p>
          <div className="dgw-hero-actions" style={{justifyContent:'center'}}>
            <Link to="/website/eu-cra" className="dgw-btn dgw-btn-primary dgw-btn-lg">EU CRA compliance →</Link>
            <Link to="/website/contact" className="dgw-btn dgw-btn-ghost dgw-btn-lg">Ask the team</Link>
          </div>
        </div>
      </section>

      {/* Core pillars */}
      <section className="dgw-section" style={{background:'#fff'}}>
        <div style={{maxWidth:1100, margin:'0 auto'}}>
          <div className="dgw-section-header" style={{marginBottom:56}}>
            <span className="dgw-section-tag">Security architecture</span>
            <h2>What trust looks like in code.</h2>
            <p className="dgw-section-lead">
              These aren't policy statements. Each one is an implementation decision
              that determines what an attacker can and cannot reach.
            </p>
          </div>
          <div style={{display:'flex', flexDirection:'column', gap:32}}>
            {PILLARS.map((p, i) => (
              <div key={p.title} className="dgw-2col" style={{
                gap:48, padding:'36px',
                borderRadius:16, background:'#f8fafc', border:'1px solid #e2e8f0',
              }}>
                <div style={{order: i % 2 === 1 ? 1 : 0}}>
                  <div style={{display:'flex', alignItems:'center', gap:12, marginBottom:16}}>
                    <span style={{
                      width:48, height:48, borderRadius:12, fontSize:22,
                      background: p.color + '15', display:'flex', alignItems:'center',
                      justifyContent:'center', flexShrink:0,
                    }}>{p.icon}</span>
                    <h3 style={{fontSize:20, fontWeight:800, color:'#0f172a', margin:0}}>{p.title}</h3>
                  </div>
                  <p style={{fontSize:15, color:'#475569', lineHeight:1.7, marginBottom:20}}>{p.body}</p>
                  <ul style={{listStyle:'none', padding:0, margin:0, display:'flex', flexDirection:'column', gap:8}}>
                    {p.points.map(pt => (
                      <li key={pt} style={{display:'flex', gap:10, fontSize:14, color:'#334155'}}>
                        <span style={{color: p.color, fontWeight:700, flexShrink:0}}>✓</span>{pt}
                      </li>
                    ))}
                  </ul>
                </div>
                <div style={{order: i % 2 === 1 ? 0 : 1}}>
                  {/* Visual accent */}
                  <div style={{
                    padding:'32px', borderRadius:14,
                    background: p.color + '08', border:`1px solid ${p.color}22`,
                    display:'flex', alignItems:'center', justifyContent:'center',
                    minHeight:160,
                  }}>
                    <span style={{fontSize:72}}>{p.icon}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Compliance mapping table */}
      <section className="dgw-section" style={{background:'#f8fafc'}}>
        <div style={{maxWidth:900, margin:'0 auto'}}>
          <div className="dgw-section-header" style={{marginBottom:40}}>
            <span className="dgw-section-tag">Compliance mapping</span>
            <h2>What DepGuard covers, and what it doesn't.</h2>
            <p className="dgw-section-lead">Honest mapping. We show partial and roadmap coverage, not just green checkmarks.</p>
          </div>
          <div style={{overflowX:'auto'}}>
            <table style={{width:'100%', borderCollapse:'separate', borderSpacing:0}}>
              <thead>
                <tr>
                  {['Framework', 'Requirement', 'Coverage'].map(h => (
                    <th key={h} style={{
                      textAlign:'left', padding:'12px 16px', fontSize:12,
                      fontWeight:700, color:'#64748b', textTransform:'uppercase',
                      letterSpacing:.5, background:'#f1f5f9',
                      borderBottom:'2px solid #e2e8f0',
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {COMPLIANCE_MAP.map((row, i) => (
                  <tr key={i} style={{background: i % 2 === 0 ? '#fff' : '#f8fafc'}}>
                    <td style={{padding:'14px 16px', fontSize:13, fontWeight:600, color:'#6366f1', borderBottom:'1px solid #f1f5f9', whiteSpace:'nowrap'}}>{row.framework}</td>
                    <td style={{padding:'14px 16px', fontSize:13, color:'#334155', borderBottom:'1px solid #f1f5f9'}}>{row.requirement}</td>
                    <td style={{padding:'14px 16px', borderBottom:'1px solid #f1f5f9'}}>{coverageChip(row.coverage)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Privacy */}
      <section className="dgw-section" style={{background:'#0f172a'}}>
        <div style={{maxWidth:860, margin:'0 auto', textAlign:'center'}}>
          <span className="dgw-section-tag" style={{background:'rgba(99,102,241,.15)', color:'#a5b4fc', borderColor:'rgba(99,102,241,.3)', marginBottom:16, display:'inline-block'}}>Data privacy</span>
          <h2 style={{color:'#f1f5f9', fontSize:'clamp(24px,3.5vw,38px)', fontWeight:800, marginBottom:20}}>
            GDPR-ready by architecture, not by policy.
          </h2>
          <p style={{color:'#94a3b8', fontSize:16, lineHeight:1.7, maxWidth:640, margin:'0 auto 40px'}}>
            Because DepGuard runs entirely in your infrastructure, your SBOM data and vulnerability 
            findings never leave your network. There is nothing for us to breach, sell, or subpoena.
            Enterprise deployments support fully air-gapped operation.
          </p>
          <div className="dgw-3col-stats" style={{maxWidth:700, margin:'0 auto'}}>
            {[
              { label: 'Data processed', value: 'In your infrastructure' },
              { label: 'External calls', value: 'OSV.dev, deps.dev only' },
              { label: 'Telemetry', value: 'Off by default' },
            ].map(s => (
              <div key={s.label} style={{padding:'20px', borderRadius:12, background:'rgba(255,255,255,.04)', border:'1px solid rgba(255,255,255,.08)'}}>
                <div style={{fontSize:12, color:'#64748b', marginBottom:4}}>{s.label}</div>
                <div style={{fontSize:14, fontWeight:700, color:'#f1f5f9'}}>{s.value}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="dgw-cta-band">
        <div className="dgw-cta-band-inner">
          <h2>Have security questions before you commit?</h2>
          <p>Our team will walk you through the architecture and answer any security review questions before you sign anything.</p>
          <div className="dgw-cta-band-actions">
            <Link to="/website/contact" className="dgw-btn-cta-white">Talk to the team</Link>
            <Link to="/website/eu-cra" className="dgw-btn-cta-ghost">EU CRA details →</Link>
          </div>
        </div>
      </section>

    </div>
  );
}
